/**
 * Maps draft brand colors into a BookingTheme object.
 *
 * Uses figgy_purple as the base and overrides pass/card tokens
 * plus link-in-bio tokens from the draft's brand colors.
 * Falls back to figgy_purple unchanged when no colors are set.
 *
 * Does NOT create a new entry in BOOKING_THEMES — returns a
 * one-off BookingTheme that flows through the same flowThemeVars()
 * and flowTailwindOverrides() pipeline as any real theme.
 */

import { BOOKING_THEMES, type BookingTheme } from '@/components/booking-flow/themes';
import type { DraftPublicView, FiggyDraft } from '@/types/draft';

// ── Luminance helpers ────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] | null {
  const raw = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(raw)) return null;
  const full = raw.length === 3
    ? raw.split('').map((c) => c + c).join('')
    : raw;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return null;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0.5;
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function isLightColor(hex: string): boolean {
  return relativeLuminance(hex) > 0.4;
}

function withAlpha(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,${alpha})`;
  const [r, g, b] = rgb;
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── Adapter ──────────────────────────────────────────────────────

type DraftColorSource = Pick<
  FiggyDraft | DraftPublicView,
  'brand_primary_color' | 'brand_secondary_color' | 'brand_accent_color'
>;

export function buildThemeFromDraft(draft: DraftColorSource): BookingTheme {
  const base = BOOKING_THEMES.figgy_purple;

  const primary = draft.brand_primary_color && hexToRgb(draft.brand_primary_color)
    ? draft.brand_primary_color
    : null;
  const accent = draft.brand_accent_color && hexToRgb(draft.brand_accent_color)
    ? draft.brand_accent_color
    : null;
  const secondary = draft.brand_secondary_color && hexToRgb(draft.brand_secondary_color)
    ? draft.brand_secondary_color
    : null;

  // No valid custom colors → use figgy_purple as-is
  if (!primary && !accent) return base;

  const bg = primary || base.bg;
  const light = isLightColor(bg);
  const fg = light ? '#1c160f' : '#F5EDE0';
  const accentColor = accent || (light ? '#5956F6' : '#F5EDE0');

  return {
    ...base,
    key: 'draft_custom',
    name: 'Draft Custom',

    // Pass / card tokens
    bg,
    bgSoft: secondary || withAlpha(bg, 0.85),
    fg,
    accent: accentColor,
    accentSoft: withAlpha(accentColor, 0.10),
    border: withAlpha(fg, 0.22),
    borderStrong: withAlpha(fg, 0.38),
    passScallop: '#F4F1EB',
    statusBarDark: !light,

    // Flow tokens — keep the cream default so steps feel consistent
    flowAccent: bg,
    flowAccentSoft: withAlpha(bg, 0.06),
    flowAccentBorder: withAlpha(bg, 0.20),

    // Link-in-bio overrides
    libCta: bg,
    libCtaFg: fg,
    libAvatarRing: withAlpha(accentColor, 0.50),
    libRingHalo: withAlpha(accentColor, 0.12),
    libFiggyColor: '#5956F6',
  };
}
